import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtDate, fmtDateTime, fmtINR } from "@/lib/api";
import { PageHeader, PrimaryButton } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DocumentTrail from "@/components/DocumentTrail";
import { AdminVoidRestore, AdminEditBanner } from "@/components/AdminActions";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { FileCheck2, CheckCircle2 } from "lucide-react";

export default function GRNDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasRole } = useAuth();
  const [g, setG] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [advOutstanding, setAdvOutstanding] = useState(0);
  const [invOpen, setInvOpen] = useState(false);
  const [invForm, setInvForm] = useState({ invoice_no: "", invoice_date: "", invoice_amount: 0, gst_amount: 0, advance_adjusted: 0, remarks: "" });

  const load = useCallback(async () => {
    const [g1, inv, adv] = await Promise.all([
      api.get(`/grns/${id}`).then((r) => r.data),
      api.get(`/grns/${id}/invoices`).then((r) => r.data).catch(() => []),
      Promise.resolve(null),
    ]);
    setG(g1); setInvoices(inv);
    if (g1?.po_id) {
      try {
        const a = await api.get(`/pos/${g1.po_id}/advance-status`);
        setAdvOutstanding(a.data?.advance_outstanding || 0);
      } catch (e) { console.warn("Failed to load advance status:", e); }
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!g) return <div>Loading…</div>;

  const canBookInv = hasRole("Administrator", "Accounts Executive", "Accounts Manager") &&
    invoices.length === 0 && !g.is_voided;

  const openBook = () => {
    // Default invoice_amount to accepted items × rate — a decent starting estimate
    const est = (g.items || []).reduce((s, it) => s + (it.accepted_qty || 0) * (it.rate || 0), 0);
    setInvForm({
      invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10),
      invoice_amount: Math.round(est * 1.18 * 100) / 100,
      gst_amount: Math.round(est * 0.18 * 100) / 100,
      advance_adjusted: 0,   // 0 → backend auto-adjust
      remarks: "",
    });
    setInvOpen(true);
  };
  const saveInv = async () => {
    if (!invForm.invoice_no) { toast.error("Invoice No is required"); return; }
    try {
      const { data } = await api.post("/invoices", {
        po_id: g.po_id, grn_id: id, ...invForm,
      });
      toast.success(`Invoice ${data.invoice_no} booked${data.three_way_match ? " — 3-way matched" : ""}`);
      setInvOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="grn-detail">
      <PageHeader
        title={<span className="font-mono">{g.grn_no}</span>}
        subtitle={`${g.vendor_name} • PO ${g.po_number}`}
        actions={
          <>
            {canBookInv && (
              <PrimaryButton onClick={openBook} data-testid="book-invoice-inline-btn">
                <FileCheck2 size={14} className="mr-2" />Book Invoice
              </PrimaryButton>
            )}
            <AdminVoidRestore entity="grn" id={id} isVoided={g.is_voided} onDone={load} />
          </>
        }
      />
      <AdminEditBanner doc={g} />
      <Card className="p-5">
        <div className="flex justify-between mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm flex-1">
            <div><div className="text-[10px] uppercase text-muted-foreground">Invoice No</div>{g.invoice_no || "-"}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Challan</div>{g.challan_no || "-"}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Vehicle</div>{g.vehicle_no || "-"}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Driver</div>{g.driver_name || "-"}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Batch</div>{g.batch_no || "-"}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Heat No</div>{g.heat_no || "-"}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Manufacturer</div>{g.manufacturer || "-"}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Received</div>{fmtDateTime(g.created_at)}</div>
          </div>
          <StatusBadge status={g.is_voided ? "Voided" : g.status} />
        </div>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Material</th>
                <th className="text-left px-3 py-2">Unit</th>
                <th className="text-right px-3 py-2">Ordered</th>
                <th className="text-right px-3 py-2">Received</th>
                <th className="text-right px-3 py-2">Accepted</th>
                <th className="text-right px-3 py-2">Rejected</th>
                <th className="text-right px-3 py-2">Balance</th>
              </tr>
            </thead>
            <tbody>
              {g.items.map((it, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{it.material_name}</td>
                  <td className="px-3 py-2">{it.unit}</td>
                  <td className="px-3 py-2 text-right">{it.ordered_qty}</td>
                  <td className="px-3 py-2 text-right">{it.received_qty}</td>
                  <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{it.accepted_qty}</td>
                  <td className="px-3 py-2 text-right text-red-700">{it.rejected_qty}</td>
                  <td className="px-3 py-2 text-right">{it.balance_qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {g.remarks && <div className="mt-4 text-sm"><span className="text-muted-foreground">Remarks: </span>{g.remarks}</div>}
      </Card>

      {/* ---- Linked Invoice ---- */}
      <Card className="p-5 mt-4" data-testid="grn-invoice-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Vendor Invoice</div>
            <div className="font-display text-lg font-semibold">
              {invoices.length === 0 ? "No invoice booked yet" : `${invoices.length} invoice${invoices.length > 1 ? "s" : ""} booked`}
            </div>
          </div>
          {canBookInv && (
            <Button variant="outline" onClick={openBook} data-testid="book-invoice-card-btn">
              <FileCheck2 size={14} className="mr-2" />Book Invoice
            </Button>
          )}
        </div>
        {invoices.length > 0 && (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Invoice No</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-right px-3 py-2">Adv. Adjusted</th>
                  <th className="text-left px-3 py-2">3-Way</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-border hover:bg-secondary/30 cursor-pointer" onClick={() => nav(`/invoices?id=${inv.id}`)}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                    <td className="px-3 py-2">{fmtDate(inv.invoice_date)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtINR(inv.invoice_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-kabir-dark">{inv.advance_adjusted > 0 ? "-" + fmtINR(inv.advance_adjusted) : "-"}</td>
                    <td className="px-3 py-2">{inv.three_way_match ? <CheckCircle2 size={16} className="text-emerald-600" /> : "—"}</td>
                    <td className="px-3 py-2"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <DocumentTrail trail={g.trail_ref} />

      {/* ---- Inline invoice dialog ---- */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent data-testid="inline-invoice-dialog">
          <DialogHeader><DialogTitle>Book Invoice against GRN {g.grn_no}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-semibold">Invoice No *</Label><Input value={invForm.invoice_no} onChange={(e) => setInvForm({ ...invForm, invoice_no: e.target.value })} data-testid="inline-inv-no" /></div>
            <div><Label className="text-xs uppercase font-semibold">Invoice Date</Label><Input type="date" value={invForm.invoice_date} onChange={(e) => setInvForm({ ...invForm, invoice_date: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Invoice Amount *</Label><Input type="number" value={invForm.invoice_amount} onChange={(e) => setInvForm({ ...invForm, invoice_amount: parseFloat(e.target.value) || 0 })} data-testid="inline-inv-amount" /></div>
            <div><Label className="text-xs uppercase font-semibold">GST Amount</Label><Input type="number" value={invForm.gst_amount} onChange={(e) => setInvForm({ ...invForm, gst_amount: parseFloat(e.target.value) || 0 })} /></div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase font-semibold">Advance to Adjust</Label>
              <Input type="number" value={invForm.advance_adjusted} onChange={(e) => setInvForm({ ...invForm, advance_adjusted: parseFloat(e.target.value) || 0 })} data-testid="inline-inv-advance" />
              <div className="text-[11px] text-muted-foreground mt-1">
                Outstanding advance on this PO: <strong className="text-amber-700">{fmtINR(advOutstanding)}</strong>.
                Leave 0 to auto-adjust the maximum possible.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={saveInv} data-testid="inline-inv-save">Book & 3-Way Match</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
